import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import './ProductSlider.css';
import { useLanguage } from '../../../contexts/LanguageContext';
import { initialProducts } from '../../../constants/products';
// import { useProductPricing } from '../../../hooks/useProductPricing';

const ProductSlider = ({
  isCallActive,
  buttonRef,
  // Props für KI-Empfehlungen (product names as strings from Gotoki)
  primaryRecommendation = null,      // Product name as string from Gotoki
  secondaryRecommendations = [],     // Array of product names as strings from Gotoki
  isProductLoading = false,          // While true, show shimmer popup and suppress grid highlights
  children                           // Children components (e.g., HeadphoneTypeSelector)
}) => {
  const sliderRef = useRef(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { translate, localize } = useLanguage();

  // Fetch dynamic pricing for all initial products
  // const { prices } = useProductPricing(initialProducts);

  // Find product in initialProducts by name (case-insensitive)
  const findProductByName = useCallback((productName) => {
    if (!productName || typeof productName !== 'string') return null;
    return initialProducts.find(p =>
      p.displayName.toLowerCase() === productName.toLowerCase()
    );
  }, []);

  // Get primary product from initialProducts
  const primaryProduct = useMemo(() => {
    if (!primaryRecommendation) return null;
    return findProductByName(primaryRecommendation);
  }, [primaryRecommendation, findProductByName]);

  // Get secondary products from initialProducts
  const secondaryProducts = useMemo(() => {
    if (!Array.isArray(secondaryRecommendations)) return [];
    return secondaryRecommendations
      .map(name => findProductByName(name))
      .filter(Boolean);
  }, [secondaryRecommendations, findProductByName]);

  // Show all initial products (pricing disabled)
  // const displayProducts = useMemo(() => {
  //   // Start with initial products and merge with API pricing
  //   return initialProducts.map(product => {
  //     // Use lowercase key for case-insensitive matching
  //     const apiPrice = prices[product.displayName.toLowerCase()];
  //
  //     // Only update originalPrice if we have API data
  //     if (apiPrice && apiPrice.price) {
  //       return {
  //         ...product,
  //         originalPrice: apiPrice.price
  //       };
  //     }
  //
  //     return product;
  //   });
  // }, [prices]);
  const displayProducts = useMemo(() => initialProducts, []);

  // Helper to check if a product is the primary recommendation
  // Suppressed while loading so the grid below stays neutral
  const isPrimaryRecommendation = useCallback((product) => {
    if (isProductLoading) return false;
    if (!primaryProduct) return false;
    // Match by ID or by name (case-insensitive)
    return product.id === primaryProduct.id ||
           product.displayName.toLowerCase() === primaryProduct.displayName.toLowerCase();
  }, [primaryProduct, isProductLoading]);

  // Helper to check if a product is an alternative recommendation
  const isAlternativeRecommendation = useCallback((product) => {
    if (isProductLoading) return false;
    if (!primaryProduct || secondaryProducts.length === 0) return false;
    // Match by ID or by name (case-insensitive)
    return secondaryProducts.some(sp =>
      product.id === sp.id ||
      product.displayName.toLowerCase() === sp.displayName.toLowerCase()
    );
  }, [primaryProduct, secondaryProducts, isProductLoading]);
  
  // Auto-öffne das Popup für die Hauptempfehlung
  // Also auto-close when the recommendation is cleared (call end, new tool_result_loading, reset)
  useEffect(() => {
    if (primaryProduct) {
      console.log('🎯 Opening detail for primary product:', primaryProduct.displayName);

      // Find the primary product in displayProducts to get the correct product object
      const primaryInDisplay = displayProducts.find(p =>
        p.id === primaryProduct.id ||
        p.displayName.toLowerCase() === primaryProduct.displayName.toLowerCase()
      );

      if (primaryInDisplay) {
        setSelectedProduct(primaryInDisplay);
      }
    } else {
      setSelectedProduct(null);
    }
  }, [primaryProduct, displayProducts]);

  const handleProductClick = (product) => {
    if (selectedProduct?.id === product.id) {
      setSelectedProduct(null);
    } else {
      setSelectedProduct(product);
    }
  };

  const handleCloseDetail = useCallback(() => {
    setSelectedProduct(null);
  }, []);

  // Escape-Taste und Klick außerhalb zum Schließen
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedProduct) {
        handleCloseDetail();
      }
    };
    
    const handleClickOutside = (e) => {
      if (!selectedProduct) return;

      // Prüfe ob der Klick innerhalb des Popups oder Sliders war
      const overlay = document.querySelector('.product-detail-overlay');
      const sliderContainer = document.querySelector('.product-slider-container');
      const gotokiButton = document.querySelector('gotoki-button');

      // Don't close if clicking the gotoki-button or inside the overlay/slider
      if (gotokiButton && gotokiButton.contains(e.target)) {
        return;
      }

      if (overlay && !overlay.contains(e.target) &&
          sliderContainer && !sliderContainer.contains(e.target)) {
        handleCloseDetail();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [selectedProduct, handleCloseDetail]);

  return (
    <div className="product-slider-container relative w-full min-h-[120px] sm:min-h-[130px] md:min-h-[140px] lg:min-h-[150px] xl:min-h-[160px] 2xl:min-h-[180px] h-auto flex flex-col items-center justify-end flex-shrink-0 overflow-visible px-0 py-3 sm:py-[22px] md:py-[25px] lg:py-5 xl:py-[25px] 2xl:py-[35px]">
      <img
        src="/Assets/welle.png"
        alt=""
        className="absolute bottom-[20px] sm:bottom-6 md:bottom-8 lg:bottom-10 xl:bottom-12 2xl:bottom-14 left-0 w-full h-[180px] sm:h-[200px] md:h-[220px] lg:h-[230px] xl:h-[250px] object-cover object-bottom z-0 pointer-events-none"
      />

      {/* Render children (e.g., HeadphoneTypeSelector) */}
      {children}

      {/* Loading shimmer overlay - same shape as the real popup, no real product yet */}
      {isProductLoading && !selectedProduct && (
        <div
          className="product-detail-overlay absolute bottom-full left-1/2 -translate-x-1/2 flex flex-col items-center z-[100000] pointer-events-none w-auto max-w-[95%] sm:max-w-[90%] md:max-w-[500px] lg:max-w-[550px] xl:max-w-[600px] px-1 sm:px-2 md:px-[10px] lg:px-[15px] xl:px-5 pb-2 sm:pb-2 md:pb-[10px] lg:pb-3 xl:pb-[15px] mb-4 max-h-[calc(100vh-200px)]"
          style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'visible' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="detail-header">
            <div className="detail-header-left">
              <span className="detail-live-dot"></span>
              <span className="detail-header-title">{translate('aiRecommendations')}</span>
            </div>
          </div>

          <div className="product-detail-panel">
            <div className="detail-content-wrapper">
              {/* Shimmer placeholder where the product image will render */}
              <div className="detail-product-image rounded-xl skeleton-shimmer" />

              <div className="detail-info-section">
                {/* Badge placeholder */}
                <span className="detail-recommendation-badge skeleton-shimmer text-transparent" aria-hidden="true">
                  &nbsp;PLACEHOLDER&nbsp;
                </span>
                {/* Product name placeholder — keeps real font metrics so size matches */}
                <h2 className="detail-product-name skeleton-shimmer text-transparent rounded" aria-hidden="true" style={{ width: '60%' }}>
                  &nbsp;
                </h2>
                {/* Description placeholder — two stacked lines */}
                <div className="flex flex-col items-center gap-2 w-full">
                  <span className="detail-description skeleton-shimmer text-transparent rounded" aria-hidden="true" style={{ width: '90%' }}>
                    &nbsp;
                  </span>
                  <span className="detail-description skeleton-shimmer text-transparent rounded" aria-hidden="true" style={{ width: '70%' }}>
                    &nbsp;
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail-Ansicht als Overlay - erscheint über dem Slider ohne Layout zu verschieben */}
      {selectedProduct && (
        <div className="product-detail-overlay absolute bottom-full left-1/2 -translate-x-1/2 flex flex-col items-center z-[100000] pointer-events-none w-auto max-w-[95%] sm:max-w-[90%] md:max-w-[500px] lg:max-w-[550px] xl:max-w-[600px] px-1 sm:px-2 md:px-[10px] lg:px-[15px] xl:px-5 pb-2 sm:pb-2 md:pb-[10px] lg:pb-3 xl:pb-[15px] mb-4 max-h-[calc(100vh-200px)]" style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'visible' }} onClick={(e) => e.stopPropagation()}>
            {/* Header mit KI Empfehlungen */}
            <div className="detail-header">
            <div className="detail-header-left">
              <span className="detail-live-dot"></span>
              <span className="detail-header-title">{translate('aiRecommendations')}</span>
            </div>
            <div className="detail-header-actions">
              <button
                className={`detail-call-toggle-btn ${isCallActive ? 'active' : 'inactive'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();

                  // Get the gotoki-button element and trigger its button
                  const gotokiButton = document.querySelector('gotoki-button');
                  if (gotokiButton && gotokiButton.shadowRoot) {
                    const innerButton = gotokiButton.shadowRoot.querySelector('button');
                    if (innerButton) {
                      innerButton.click();
                    }
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                {isCallActive ? translate('pauseCall') : translate('startCall')}
              </button>
              <button className="detail-close-x" onClick={handleCloseDetail} aria-label="Schließen">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Produkt Detail Panel */}
          <div className="product-detail-panel">
            <div className="detail-content-wrapper">
              <img src={selectedProduct.variantImage || selectedProduct.image} alt={selectedProduct.displayName} className="detail-product-image" />
              
              <div className="detail-info-section">
                {/* Badge im Detail-Panel - nur für Hauptempfehlung */}
                {selectedProduct.id === primaryProduct?.id && (
                  <span className="detail-recommendation-badge primary">⭐ {translate('topRecommendation')}</span>
                )}
                <h2 className="detail-product-name">{selectedProduct.displayName}</h2>
                {/* Price display disabled
                <div className="detail-price-row">
                  {selectedProduct.originalPrice && (
                    <span className="detail-original-price">{selectedProduct.originalPrice}</span>
                  )}
                  <span className="detail-price">{selectedProduct.price}</span>
                </div>
                */}
                <p className="detail-description">{localize(selectedProduct.shortDescription)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Product Grid - Shows all products in a grid, 12 columns when space allows (single row), wraps to 6 columns (2 rows) on smaller screens, highlights recommended ones */}
      <div className="product-grid relative z-[1] flex flex-wrap justify-center gap-3 sm:gap-[15px] md:gap-[18px] lg:gap-5 xl:gap-6 2xl:gap-[30px] px-4 sm:px-[30px] md:px-[40px] lg:px-[50px] xl:px-[60px] 2xl:px-20 pt-[10px] sm:pt-2 md:pt-2 lg:pt-[10px] xl:pt-3 2xl:pt-4 pb-3 sm:pb-4 md:pb-[18px] lg:pb-5 xl:pb-5 2xl:pb-[25px] max-w-full items-start" ref={sliderRef}>
        {displayProducts.map((product) => {
          const isPrimary = isPrimaryRecommendation(product);
          const isAlternative = isAlternativeRecommendation(product);
          const isSelected = selectedProduct?.id === product.id;

          return (
            <div
              key={product.id}
              data-product-id={product.id}
              className={`slider-product-item relative flex flex-col items-center gap-2 cursor-pointer transition-all duration-300 px-1.5 sm:px-2 py-1.5 sm:py-2 pt-[18px] mt-[5px] rounded-xl sm:rounded-xl border-2 ${isSelected ? 'border-brand-orange bg-brand-orange/10' : 'border-transparent'} ${isPrimary ? 'recommendation-primary' : ''} ${isAlternative ? 'recommendation-secondary' : ''} hover:-translate-y-2 sm:hover:-translate-y-[8px]`}
              onClick={() => handleProductClick(product)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleProductClick(product)}
            >
              {/* Empfehlungs-Badge - show when product is recommended */}
              {isPrimary && (
                <span className="recommendation-badge primary">{translate('topRecommendation')}</span>
              )}
              {isAlternative && (
                <span className="recommendation-badge secondary">{translate('alternative')}</span>
              )}

              <div className="w-[50px] h-[50px] sm:w-[55px] sm:h-[55px] md:w-[60px] md:h-[60px] lg:w-[65px] lg:h-[65px] xl:w-[70px] xl:h-[70px] 2xl:w-20 2xl:h-20 flex items-center justify-center">
                <img
                  src={product.image}
                  alt={product.displayName}
                  className="max-w-full max-h-[50px] sm:max-h-[55px] md:max-h-[60px] lg:max-h-[65px] xl:max-h-[70px] 2xl:max-h-20 object-contain opacity-85 transition-all duration-300 [filter:drop-shadow(0_4px_12px_rgba(0,0,0,0.4))] hover:opacity-100 hover:[filter:drop-shadow(0_10px_25px_rgba(255,106,42,0.4))]"
                />
              </div>
              <span className="font-mont text-[0.55rem] sm:text-[0.6rem] md:text-[0.65rem] lg:text-[0.7rem] xl:text-[0.7rem] 2xl:text-xs font-semibold text-white/80 text-center transition-colors duration-300 whitespace-nowrap max-w-[60px] sm:max-w-[70px] md:max-w-20 lg:max-w-20 xl:max-w-[90px] 2xl:max-w-[100px] overflow-hidden text-ellipsis group-hover:text-brand-orange">{product.displayName}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductSlider;

